import {
  outstandingOf,
  overdueDaysOf,
  overdueSinceOf,
  paidAmountOf,
  paidPctOf,
  settlementStatusOf,
  signedOutstandingOf,
  signedPaidAmountOf,
  signedSumEntries,
  type SettlementStatus,
} from '@progear/finance-core';
import type { CashFlowEntry } from '@/lib/cashFlow';

/**
 * The settlement read-model shared by BOTH cash-flow lenses (L14): the register's
 * status-cell hover and the calendar's `DocHoverCard`. Every number is selected
 * straight from `finance-core` — this builder NEVER does its own money maths
 * (L9). The only choice it makes is *which* finance-core selector to read:
 *
 * - `signed: true`  (register lens, mixed directions) → income reads `+`, a cost
 *   reads `−` via `signedSumEntries` / `signedPaidAmountOf` / `signedOutstandingOf`.
 * - `signed: false` (calendar lens, single direction)  → plain magnitudes via
 *   `e.money.gross` / `paidAmountOf` / `outstandingOf`.
 *
 * The status (`settlementStatusOf`) and paid share (`paidPctOf`) are direction-
 * agnostic, so both lenses read the identical badge + ring.
 */
export interface SettlementView {
  status: SettlementStatus;
  isIncome: boolean;
  /** Gross — signed when `signed`, otherwise the magnitude. */
  gross: number;
  /** "Tasutud" — signed when `signed`, otherwise the magnitude. */
  paid: number;
  /** "Jääk" (outstanding) — signed when `signed`, otherwise the magnitude. */
  outstanding: number;
  /** Whole-percent cash paid / gross (`paidPctOf`); may exceed 100 when overpaid. */
  paidPct: number;
  paymentCount: number;
  lastPaidOn: string | null;
  /** Due date (`expectedDate`) — the "Tähtaeg" row, shown in both lenses. */
  expectedDate: string;
  /** Legacy realised/cleared date — the "Viimati makstud" fallback when there are no payment events. */
  actualDate: string | null;
  /** Overdue age, present ONLY when the status is `overdue`: whole days past the
   *  ORIGINAL due day (`overdueDaysOf`) + that origin day (`overdueSinceOf` — a
   *  reschedule/split keeps `originExpectedDate`, so the age never resets when the
   *  document is pushed forward). Both lenses render the same "Üle tähtaja N p ·
   *  alates …" row from it (L14); the figures are finance-core reads (no maths here). */
  overdueAge: { days: number; since: string } | null;
  signed: boolean;
}

export function settlementView(e: CashFlowEntry, today: string, signed: boolean): SettlementView {
  const status = settlementStatusOf(e, today);
  return {
    status,
    isIncome: e.kind === 'income',
    gross: signed ? signedSumEntries([e]).gross : e.money.gross,
    paid: signed ? signedPaidAmountOf(e) : paidAmountOf(e),
    outstanding: signed ? signedOutstandingOf(e) : outstandingOf(e),
    paidPct: paidPctOf(e),
    paymentCount: e.paymentCount,
    lastPaidOn: e.lastPaidOn,
    expectedDate: e.expectedDate,
    actualDate: e.actualDate,
    overdueAge:
      status === 'overdue' ? { days: overdueDaysOf(e, today), since: overdueSinceOf(e) } : null,
    signed,
  };
}
